import { useEffect, useState } from 'react';
import {
  EXPENSE_CATEGORIES,
  InvoiceReceivedCsvRowSchema,
  type ExpenseCategory,
  type ImputationType,
  type PublicUser,
} from '@otter/shared';
import { api, downloadFile } from '../lib/api';
import { DateInput } from '../components/DateInput';
import { parseCsv } from '../lib/csv';
import { fmt, fmtDate, todayIso } from '../lib/format';

type Props = {
  user: PublicUser;
  scope: string;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type Row = {
  id: string;
  supplier: string;
  reference: string | null;
  date: string;
  category: ExpenseCategory;
  baseAmount: string;
  ivaAmount: string;
  totalAmount: string;
  imputationType: ImputationType;
  clientId: string | null;
  clientName: string | null;
  ownerCode: string | null;
  hasAttachment: boolean;
  isPrivate: boolean;
};

const CATS: Array<{ id: 'all' | ExpenseCategory; label: string }> = [
  { id: 'all', label: 'todas' },
  ...EXPENSE_CATEGORIES.map((c) => ({ id: c, label: c.toLowerCase() })),
];

export function FacturasRecibidas({ user, scope, onToast, onError }: Props) {
  const [items, setItems] = useState<Row[]>([]);
  const [category, setCategory] = useState<'all' | ExpenseCategory>('all');
  const [show, setShow] = useState(false);

  async function load() {
    try {
      const q: Record<string, string> = {};
      if (category !== 'all') q.category = category;
      const res = await api<{ items: Row[] }>('/admin/invoices-received', { query: q });
      setItems(res.items);
    } catch (e) {
      onError(e);
    }
  }

  // Admin-only: flip the private flag on an existing expense. Optimistic, with
  // revert on failure (received invoices have no full edit form).
  async function togglePrivate(row: Row) {
    const next = !row.isPrivate;
    setItems((items) => items.map((it) => (it.id === row.id ? { ...it, isPrivate: next } : it)));
    try {
      await api(`/admin/invoices-received/${row.id}`, {
        method: 'PATCH',
        body: { isPrivate: next },
      });
      onToast(next ? 'Gasto marcado como privado' : 'Gasto visible para el equipo', 'ok');
    } catch (e) {
      setItems((items) => items.map((it) => (it.id === row.id ? { ...it, isPrivate: !next } : it)));
      onError(e);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, scope]);

  return (
    <div className="body">
      <div className="pageh">
        <h1>Facturas recibidas</h1>
        <div className="sub">
          {items.length} gasto{items.length === 1 ? '' : 's'}
        </div>
        <div className="actions">
          <input
            type="file"
            accept=".csv"
            id="csv-import-re"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const { valid } = await parseCsv(f, InvoiceReceivedCsvRowSchema);
                if (valid.length === 0) return;
                const r = await api<{ created: number; errors: unknown[] }>(
                  '/admin/invoices-received/import-csv',
                  { method: 'POST', body: { rows: valid } },
                );
                onToast(`Import: ${r.created} creados`);
                void load();
              } catch (err) {
                onError(err);
              } finally {
                e.target.value = '';
              }
            }}
          />
          <button
            className="btn"
            onClick={() => downloadFile('/admin/invoices-received/export-csv', 'invoices-received.csv')}
          >
            Export CSV
          </button>
          <button className="btn" onClick={() => document.getElementById('csv-import-re')?.click()}>
            Import CSV
          </button>
          <button className="btn primary" onClick={() => setShow(true)}>
            <span className="plus">+</span> Nuevo gasto
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="chips">
          {CATS.map((c) => (
            <button
              key={c.id}
              className={`chip ${category === c.id ? 'on' : ''}`}
              onClick={() => setCategory(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Ref</th>
              <th>Fecha</th>
              <th>Categoría</th>
              <th>Imputación</th>
              <th className="num">Base</th>
              <th className="num">IVA</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td data-label="Proveedor">
                  <b>{r.supplier}</b>
                  {user.isSystemAdmin ? (
                    <button
                      type="button"
                      className={`privada-toggle${r.isPrivate ? ' on' : ''}`}
                      onClick={() => void togglePrivate(r)}
                      title={
                        r.isPrivate
                          ? 'Privada — clic para hacerla visible al equipo'
                          : 'Hacer privada (solo el admin la verá)'
                      }
                    >
                      {r.isPrivate ? 'privada' : 'ocultar'}
                    </button>
                  ) : (
                    r.isPrivate && <span className="privada-tag">privada</span>
                  )}
                </td>
                <td className="mono" data-label="Ref">{r.reference ?? '—'}</td>
                <td className="mono" data-label="Fecha">{fmtDate(r.date)}</td>
                <td data-label="Categoría">{r.category}</td>
                <td data-label="Imputación">
                  {r.imputationType === 'estructura' ? (
                    <span style={{ color: 'var(--muted)' }}>estructura</span>
                  ) : (
                    <span>
                      <span className="av-mini av-A">{r.ownerCode ?? '?'}</span> {r.clientName}
                    </span>
                  )}
                </td>
                <td className="num" data-label="Base">{fmt(r.baseAmount)}</td>
                <td className="num" data-label="IVA">{fmt(r.ivaAmount)}</td>
                <td className="num" data-label="Total">{fmt(r.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty">sin gastos</div>}
      </div>

      {show && (
        <CreateReceivedModal
          isAdmin={user.isSystemAdmin}
          onClose={() => setShow(false)}
          onCreated={() => {
            setShow(false);
            void load();
            onToast('Gasto registrado', 'ok');
          }}
          onError={onError}
        />
      )}
    </div>
  );
}

function CreateReceivedModal(props: {
  isAdmin: boolean;
  onClose: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const [supplier, setSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(todayIso());
  const [category, setCategory] = useState<ExpenseCategory>('Software');
  const [baseAmount, setBaseAmount] = useState(0);
  const [ivaAmount, setIvaAmount] = useState(0);
  const [imputationType, setImputationType] = useState<ImputationType>('estructura');
  const [clientId, setClientId] = useState<string>('');
  const [clients, setClients] = useState<Array<{ id: string; name: string; nif: string }>>([]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<{ items: Array<{ id: string; name: string; nif: string }> }>('/admin/clients').then(
      (r) => setClients(r.items),
    );
  }, []);

  const totalAmount = baseAmount + ivaAmount;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/admin/invoices-received', {
        method: 'POST',
        body: {
          supplier,
          reference: reference || null,
          date,
          category,
          baseAmount,
          ivaAmount,
          totalAmount,
          imputationType,
          clientId: imputationType === 'cliente' ? clientId : null,
          hasAttachment: false,
          isPrivate,
        },
      });
      props.onCreated();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Nuevo gasto</h3>
        <div className="field"><label>Proveedor</label><input value={supplier} onChange={(e) => setSupplier(e.target.value)} required /></div>
        <div className="field"><label>Referencia</label><input value={reference} onChange={(e) => setReference(e.target.value)} /></div>
        <div className="field"><label>Fecha</label><DateInput value={date} onChange={setDate} required /></div>
        <div className="field">
          <label>Categoría</label>
          <select value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="field"><label>Base</label><input type="number" step="0.01" value={baseAmount} onChange={(e) => setBaseAmount(Number.parseFloat(e.target.value) || 0)} required /></div>
        <div className="field"><label>IVA</label><input type="number" step="0.01" value={ivaAmount} onChange={(e) => setIvaAmount(Number.parseFloat(e.target.value) || 0)} /></div>
        <div className="field">
          <label>Imputación</label>
          <select value={imputationType} onChange={(e) => setImputationType(e.target.value as ImputationType)}>
            <option value="estructura">estructura</option>
            <option value="cliente">cliente</option>
          </select>
        </div>
        {imputationType === 'cliente' && (
          <div className="field">
            <label>Cliente</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
              <option value="">— selecciona —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        {props.isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            <span>Privada — solo el admin la ve</span>
          </label>
        )}
        <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--muted)' }}>
          Total: {fmt(totalAmount)}
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={busy}>Crear</button>
        </div>
      </form>
    </div>
  );
}
